/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { useTranslation } from 'react-i18next';

import { Button } from '@/design-system';
import { config } from '@/lib/config';
import { formatReleaseDate, LATEST_RELEASE_DATE } from '@/features/releaseNotes/data';
import { ShellDialog } from './ShellDialog';

// Product name shown in About; deliberately not VITE_APP_NAME, which carries the "Manufacturing" suffix.
const APP_NAME = 'eDiagnostics';

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="about-row">
      <dt className="about-k">{label}</dt>
      <dd className="about-v">{value}</dd>
    </div>
  );
}

/** About dialog: describes the eDiagnostics application and shows its build metadata. */
export function AboutModal({ onClose }: { onClose: () => void }) {
  const { t, i18n } = useTranslation();

  return (
    <ShellDialog
      onClose={onClose}
      icon="info"
      title={t('about.title')}
      size="sm"
      className="about-modal"
      footer={
        <Button type="button" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      <p className="about-desc">{t('about.description')}</p>
      <dl className="about-body">
        <Row label={t('about.labels.application')} value={APP_NAME} />
        <Row label={t('about.labels.version')} value={config.appVersion} />
        <Row label={t('about.labels.build')} value={config.buildNumber} />
        <Row
          label={t('about.labels.released')}
          value={formatReleaseDate(LATEST_RELEASE_DATE, i18n.language)}
        />
      </dl>
    </ShellDialog>
  );
}
